import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@muzly/lib/utils';

import { clampNum } from '@muzly/voice-engine';

import { GRADE_LABEL_KEY } from '../../metricGrades';

/**
 * 결과 화면의 한 줄 — 라벨 · 값 · (설명).
 *
 * **값이 없으면 "이번엔 못 쟀어요"다.** 이 판정을 줄 컴포넌트에 두는 이유: 섹션마다 다시
 * 쓰면 한 군데가 0이나 빈 문자열로 새고, 그 화면만 "0점"이라 말하게 된다.
 */

interface ResultRowProps {
  label: string;
  /** null·undefined = 안 쟀다. 0은 값이므로 문자열로 넘어온다. */
  value: string | null;
  note?: string | null;
  /** 값 오른쪽 배지(예: 미보정 기준). */
  badge?: ReactNode;
  /**
   * 값과 설명 사이에 들어가는 그림(미터 막대 등). **값이 없으면 넘기지 않는다** —
   * 위치 없는 마커는 0을 가리키는 것처럼 보인다(줄의 "이번엔 못 쟀어요"와 뜻이 어긋난다).
   */
  children?: ReactNode;
}

export const ResultRow = ({
  label,
  value,
  note,
  badge,
  children,
}: ResultRowProps) => {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-1 border-t border-border py-3 first:border-t-0 first:pt-0">
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-sm text-muted-foreground">{label}</span>
        <span className="flex items-baseline gap-2">
          {badge}
          <span
            className={
              value === null
                ? 'text-sm text-muted-foreground'
                : 'text-base font-semibold tabular-nums'
            }
          >
            {value ?? t('web.result.pending')}
          </span>
        </span>
      </div>
      {children}
      {note && (
        <p className="text-xs leading-relaxed text-muted-foreground">{note}</p>
      )}
    </div>
  );
};

/**
 * 줄 안에 들어가는 막대 — 값의 자리를 점으로, (있으면) 적정 범위를 색칠한 구간으로 그린다.
 *
 * **값이 아니라 위치(0..1)를 받는다.** 축이 뒤집힌 미터가 있어서다(§8.9 울림 넓이는
 * 대역폭이 클수록 왼쪽). 뒤집기를 미터가 알면 화면마다 방향이 갈리므로, 자·뒤집기는
 * `resultCopy`의 순수 함수가 하고 여기는 그리기만 한다.
 *
 * 값을 이미 글자로 냈으니 `aria-hidden`이다 — 읽는 사람에게 같은 말을 두 번 하지 않는다.
 */
export const ResultMeter = ({
  position,
  good,
}: {
  position: number;
  /** 적정 범위를 **위치로** 받는다(0..1). 없으면 막대에 좋은 구간 표시가 없다. */
  good?: { from: number; to: number };
}) => (
  <div className="relative my-1.5 h-1.5 rounded-full bg-muted" aria-hidden>
    {good && (
      <div
        className="absolute inset-y-0 rounded-full bg-primary/25"
        style={{
          left: `${good.from * 100}%`,
          right: `${(1 - good.to) * 100}%`,
        }}
      />
    )}
    <div
      className="absolute top-1/2 h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-popover bg-primary"
      style={{ left: `${position * 100}%` }}
    />
  </div>
);

/**
 * 값 칸이 없는 줄 — **문장 자체가 결과**인 항목(구간 경향·박자).
 * 여기에 빈 값을 넘겨 "이번엔 못 쟀어요"를 띄우면, 판정이 나와 있는 항목이 미측정으로 읽힌다.
 */
export const ResultNoteRow = ({
  label,
  note,
}: {
  label: string;
  note: string;
}) => (
  <div className="flex flex-col gap-1 border-t border-border py-3 first:border-t-0 first:pt-0">
    <span className="text-sm text-muted-foreground">{label}</span>
    <p className="text-sm leading-relaxed">{note}</p>
  </div>
);

/** 값 옆에 다는 작은 배지 — 임계값이 아직 보정 전이라는 표시에 쓴다. */
export const UncalibratedBadge = () => {
  const { t } = useTranslation();
  return (
    <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
      {t('web.result.uncalibrated')}
    </span>
  );
};

/**
 * 근거가 견고한 항목의 배지. 지금 이걸 다는 건 **노래 음정 정확도 하나뿐**이다 —
 * 정답 멜로디의 각 음과 맞대어 잰 값이라 축 카드(프록시)와 근거의 세기가 다르다.
 * 라벨은 축 카드와 같은 출처(`GRADE_LABEL_KEY`)를 쓴다 — 같은 등급이 화면마다 다른 말이 되면 안 된다.
 */
export const SolidBadge = () => {
  const { t } = useTranslation();

  return (
    <span className="rounded-full bg-primary/15 px-2 py-0.5 text-[11px] font-medium text-primary">
      {t(GRADE_LABEL_KEY.solid)}
    </span>
  );
};

export const ResultSection = ({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) => (
  <section className="rounded-2xl border border-border bg-popover p-5">
    <h3 className="text-sm font-bold">{title}</h3>
    <div className="mt-3">{children}</div>
  </section>
);

/**
 * 항목 행(시안 §8.3 `항목별 설명`, §8.7 `구간별 안정도`) — 44px 흰 원 아이콘 · 제목(16)
 * · 설명(13) · 오른쪽 부속(배지·점수). 상세 7탭이 같은 행을 쓴다.
 */
export const ItemRow = ({
  icon,
  title,
  desc,
  right,
}: {
  icon?: ReactNode;
  title: ReactNode;
  desc?: ReactNode;
  right?: ReactNode;
}) => (
  <div className="flex items-center justify-between gap-3 px-[22px] py-2">
    <div className="flex min-w-0 items-center gap-3.5">
      {icon && (
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white shadow-chip">
          {icon}
        </span>
      )}
      <div className="flex min-w-0 flex-col gap-1.5">
        <p className="text-base font-semibold tracking-[-0.08px] text-bk-900">
          {title}
        </p>
        {desc && (
          <p className="text-[13px] font-medium tracking-[-0.065px] text-bk-700">
            {desc}
          </p>
        )}
      </div>
    </div>
    {/* 값은 안 줄어든다 — `shrink` 를 열어 두면 왼쪽 설명이 길 때 값이 눌려
        `바리톤 계열`·`측정 준비 중`이 두 줄로 꺾였다(2026-09-16 실기기). 줄바꿈이
        필요하면 설명 쪽이 한다. */}
    {right && <div className="shrink-0 text-right">{right}</div>}
  </div>
);

/**
 * 농도 모드에서 가장 약한 값의 불투명도. 0이면 바닥값의 표식이 흰 바탕에 사라져 "없다"로
 * 읽힌다 — 약해도 잰 값이다.
 */
const INTENSITY_FLOOR = 0.15;

/** 위치(0..1) → 표식 색의 불투명도. 셀수록 진하다. */
const meterIntensity = (position: number): number =>
  INTENSITY_FLOOR + (1 - INTENSITY_FLOOR) * clampNum(position, 0, 1);

/**
 * 눈금 있는 미터(시안 §8.8 `핵심 포인트`) — 회색 트랙 위 적정 구간(연분홍)과 마커, 아래에
 * 눈금 라벨과 캡션. `ResultMeter`처럼 **값이 아니라 위치(0..1)** 를 받는다.
 *
 * `intensity`면 **색의 세기로** 말한다(TestFlight #58): 트랙이 옅은 색에서 진한 색으로 번지고,
 * 표식은 그 자리의 농도로 칠해진다 — 적정 구간은 칠 대신 테두리로 둔다(농도를 가리지 않게).
 * "얼마나 센가"를 재는 줄(공명 세기)에만 쓴다. "어디인가"(중심 주파수)를 농도로 말하면
 * 높은 주파수가 "더 좋다"로 읽힌다.
 */
export const TickMeter = ({
  position,
  good,
  ticks,
  caption,
  intensity = false,
}: {
  position: number;
  good?: { from: number; to: number };
  ticks: { at: number; label: string }[];
  caption?: string;
  intensity?: boolean;
}) => (
  <div className="w-[120px] shrink-0" aria-hidden>
    <div
      className={cn(
        'relative rounded-full',
        intensity ? 'h-2.5' : 'h-1.5 bg-bk-200',
      )}
      style={
        intensity
          ? {
              background: `linear-gradient(to right, hsl(var(--primary) / ${INTENSITY_FLOOR}), hsl(var(--primary)))`,
            }
          : undefined
      }
    >
      {good && (
        <div
          className={cn(
            'absolute rounded-full',
            intensity
              ? '-inset-y-1 border border-dashed border-bk-500'
              : 'inset-y-0 bg-primary/25',
          )}
          style={{
            left: `${good.from * 100}%`,
            right: `${(1 - good.to) * 100}%`,
          }}
        />
      )}
      <div
        data-testid="meter-dot"
        data-intensity={
          intensity ? meterIntensity(position).toFixed(2) : undefined
        }
        className={cn(
          'absolute top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full ring-2 ring-white',
          // 트랙과 같은 농도라 흰 고리만으로는 약한 쪽에서 묻힌다 — 진한 테두리로 가른다.
          intensity ? 'h-4 w-4 border-2 border-primary' : 'h-3 w-3 bg-primary',
        )}
        style={{
          left: `${position * 100}%`,
          ...(intensity
            ? {
                backgroundColor: `hsl(var(--primary) / ${meterIntensity(position)})`,
              }
            : {}),
        }}
      />
    </div>
    <div className="relative mt-1.5 h-4 text-[11px] text-bk-600">
      {ticks.map((tick) => (
        <span
          key={tick.label}
          className="absolute -translate-x-1/2 whitespace-nowrap"
          style={{
            left: `clamp(10px, ${tick.at * 100}%, calc(100% - 10px))`,
          }}
        >
          {tick.label}
        </span>
      ))}
    </div>
    {caption && (
      <p className="mt-0.5 text-center text-[11px] text-bk-600">{caption}</p>
    )}
  </div>
);
